import {
  type TooltipModel,
  type ScriptableTooltipContext,
  type ChartDataset,
} from "chart.js";
import { compact, isEqual, isNil } from "lodash";
import moment from "moment";
import { renderToString } from "react-dom/server";

import { PacingStatus } from "@features/mmm/components/shared/pacing-status";
import { buildLegend } from "@features/sales-v3/utils/tooltip";
import { DesignElementManager } from "@hooks/use-design-element-manager";
import { PLATFORM_NORTHBEAM } from "@north-beam/nb-common";

import {
  LOWER_MODIFIER,
  UPPER_MODIFIER,
  checkIfClose,
  createPolylineEl,
  getMinMaxOfDatasets,
} from "../chart-utils";

import {
  ACTUAL_ID,
  BUDGETED_ID,
  LIVE_FORECAST_COLOR,
  LIVE_FORECAST_ID,
} from "./const";

const designElementManager = new DesignElementManager();

export function chartTooltip(
  context: ScriptableTooltipContext<"line">,
  formatUserCurrency: (val: number) => string,
) {
  const chart = context.chart;
  const tooltip = context.tooltip;
  let tooltipEl = document.getElementById("chartjs-tooltip");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.innerHTML = "<div></div>";
    document.body.appendChild(tooltipEl);
  }
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = "0";
    return;
  }

  // tooltip hasn't moved, don't update
  if (
    tooltipEl.dataset.xPosition === String(tooltip.x) &&
    tooltipEl.dataset.yPosition === String(tooltip.y)
  ) {
    return;
  } else {
    tooltipEl.dataset.xPosition = String(tooltip.x);
    tooltipEl.dataset.yPosition = String(tooltip.y);
  }

  if (tooltip.body) {
    const content = contentForDefaultTile(tooltip, formatUserCurrency);
    const arrowPosition = {
      left: "left-0 translate-x-[-50%]",
      center: "right-0 translate-x-[50%]",
      right: "right-0 translate-x-[50%]",
    }[tooltip.xAlign];

    const contentPosition = {
      left: "translate-x-[-30px]",
      center: "translate-x-[50%]",
      right: "translate-x-[30px]",
    }[tooltip.xAlign];

    tooltipEl.innerHTML = `
      <div class="relative">
        <div
          data-tooltip-id="content"
          class="
            bg-v2-primary border border-v2-secondary py-3 px-4 rounded-md
            shadow whitespace-nowrap ${contentPosition}
            flex items-center space-x-2
          "
        >
          ${buildMiniChartContent(tooltip)}
          <div class="space-y-1">
            <div class="font-bold text-lg leading-4">Spend</div>
            ${content}
          </div>
        </div>
        <div
          data-tooltip-id="arrow"
          class="
            h-3 w-3 bg-v2-primary absolute bottom-[-6px] border-t border-l border-v2-secondary
            ${arrowPosition} rotate-[225deg]
          "
        >
        </div>
      </div>
    `;
  }

  const position = chart.canvas.getBoundingClientRect();

  const translateX = { left: "0%", center: "-100%", right: "-100%" }[
    tooltip.xAlign
  ];

  Object.assign(tooltipEl.style, {
    position: "absolute",
    opacity: "1",
    zIndex: "3000",
    pointerEvents: "none",
    paddingBottom: "12px",
    transform: `translate(${translateX}, -100%)`,
    top: `${position.top + window.scrollY + tooltip.caretY}px`,
    left: `${position.left + window.scrollX + tooltip.caretX}px`,
  });

  const contentEl = tooltipEl.querySelector("[data-tooltip-id='content']");
  const arrowEl = tooltipEl.querySelector("[data-tooltip-id='arrow']");

  if (contentEl && arrowEl) {
    const contentRect = contentEl.getBoundingClientRect();

    if (contentRect.left < 0) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("left-0", "translate-x-[-50%]");

      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[-30px]");
      tooltipEl.style.transform = "translate(0%, -100%)";
    } else if (contentRect.right > window.innerWidth) {
      arrowEl.classList.remove(
        "left-0",
        "right-0",
        "translate-x-[50%]",
        "translate-x-[-50%]",
      );
      arrowEl.classList.add("right-0", "translate-x-[50%]");

      contentEl.classList.remove(
        "translate-x-[30px]",
        "translate-x-[-30px]",
        "translate-x-[50%]",
      );
      contentEl.classList.add("translate-x-[30px]");
    }
  }
}

const getDataset = (tooltip: TooltipModel<any>, lineType: string) => {
  const currentPoint = tooltip.dataPoints[0];
  if (!currentPoint) {
    return undefined;
  }

  const { metadata } = currentPoint.dataset;
  return tooltip.chart.data.datasets.find((d) => {
    if (d.hidden) {
      return false;
    }

    const dMetadata = d.metadata;
    return (
      isEqual(dMetadata.breakdowns, metadata.breakdowns) &&
      dMetadata.lineType === lineType
    );
  });
};

const createDatasetTooltip = (
  dataset: ChartDataset<"line", any>,
  dataIndex: number,
  formatUserCurrency: (val: number) => string,
) => {
  // @ts-ignore
  const metadata = dataset.metadata;
  const value = dataset.data[dataIndex]?.y;
  if (isNil(value)) {
    return "";
  }

  const legend = buildLegend(
    {
      borderColor: metadata.color,
      backgroundColor: metadata.color,
      borderDash: metadata.borderDash ?? [],
    },
    false,
  );

  return `
    <div class="text-xs flex items-center justify-between gap-12">
      <div class="flex items-center gap-1.5">
        ${legend}
        <div>${metadata.lineType}</div>
      </div>
      <div class="font-semibold text-sm" style="color:${
        metadata.color
      };">${formatUserCurrency(value)}</div>
    </div>
  `;
};

const buildMiniChartContent = (tooltip: TooltipModel<any>) => {
  const actualDataset = getDataset(tooltip, ACTUAL_ID);
  const budgetedDataset = getDataset(tooltip, BUDGETED_ID);
  const liveForecastDataset = getDataset(tooltip, LIVE_FORECAST_ID);
  if (!actualDataset && !budgetedDataset) {
    return "";
  }

  const { dataIndex } = tooltip.dataPoints[0];
  const minMax = getMinMaxOfDatasets(
    compact([actualDataset, budgetedDataset, liveForecastDataset]),
    dataIndex,
  );
  const actualVal =
    actualDataset?.data?.[dataIndex]?.y ||
    liveForecastDataset?.data?.[dataIndex]?.y;
  const budgetedVal = budgetedDataset?.data?.[dataIndex]?.y;
  const isClose = checkIfClose(actualVal, budgetedVal);

  let budgetedModifier = 1;
  if (isClose) {
    if (actualVal < budgetedVal) {
      budgetedModifier = UPPER_MODIFIER;
    } else {
      budgetedModifier = LOWER_MODIFIER;
    }
  }

  let actualModifier = 1;
  if (isClose) {
    if (actualVal < budgetedVal) {
      actualModifier = LOWER_MODIFIER;
    } else {
      actualModifier = UPPER_MODIFIER;
    }
  }
  const content = `
    <svg viewBox="0 0 100 100" class="h-28 w-28 rounded-md border border-v2-secondary">
      ${
        budgetedDataset
          ? createPolylineEl(
              dataIndex,
              minMax,
              [budgetedDataset],
              budgetedModifier,
              {
                stroke: budgetedDataset.metadata.color,
                "stroke-dasharray":
                  budgetedDataset.metadata.borderDash?.join(",") ?? "",
              },
            )
          : ""
      }
         ${
           liveForecastDataset
             ? createPolylineEl(
                 dataIndex,
                 minMax,
                 [liveForecastDataset],
                 actualModifier,
                 { stroke: LIVE_FORECAST_COLOR, "stroke-dasharray": "6,2" },
               )
             : ""
         }
      ${
        actualDataset
          ? createPolylineEl(
              dataIndex,
              minMax,
              [actualDataset],
              actualModifier,
              {
                stroke: actualDataset.metadata.color,
                "stroke-dasharray":
                  actualDataset.metadata.borderDash?.join(",") ?? "",
              },
            )
          : ""
      }
    </svg>
  `;

  return content;
};

const contentForDefaultTile = (
  tooltip: TooltipModel<"line">,
  formatUserCurrency: (val: number) => string,
) => {
  const currentPoint = tooltip.dataPoints[0];
  if (!currentPoint) {
    return "";
  }

  const actualDataset = getDataset(tooltip, ACTUAL_ID);
  const budgetedDataset = getDataset(tooltip, BUDGETED_ID);
  const liveForecastDataset = getDataset(tooltip, LIVE_FORECAST_ID);

  if (!actualDataset && !budgetedDataset && !liveForecastDataset) {
    return "";
  }

  const { metadata } = actualDataset ?? {};
  const breakdowns = metadata?.breakdowns ?? {};
  const platform = breakdowns[PLATFORM_NORTHBEAM] ?? null;

  let icon = "";
  if (platform) {
    const designElement = designElementManager.getDesignElement(platform);
    icon = designElement.getIcon({
      borderColor: "#C1C5CD",
      size: "18",
    });
  }

  const actual = actualDataset?.data?.[currentPoint.dataIndex]?.y;

  const budgeted = budgetedDataset?.data?.[currentPoint.dataIndex]?.y;

  // PacingStatus
  let result = `
    <div class="text-subtitle text-sm flex justify-between items-center pb-3">
      ${moment.utc(currentPoint.label).format("ddd, MMMM DD, YYYY")}
      ${
        actual !== undefined && budgeted !== undefined
          ? renderToString(
              <PacingStatus
                className="px-1 py-0 ml-4"
                style={{ fontSize: "10px" }}
                actual={actual}
                budgeted={budgeted}
              />,
            )
          : ""
      }
    </div>
    ${
      icon
        ? `
        <div class="flex items-center space-x-2 text-xs">
          ${icon}
          <div class="max-w-[200px] truncate">${metadata.label}</div>
        </div>
      `
        : ""
    }
  `;

  // Larger value goes on top to match the chart
  const datasets =
    actual > budgeted
      ? compact([actualDataset, liveForecastDataset, budgetedDataset])
      : compact([budgetedDataset, actualDataset, liveForecastDataset]);

  datasets.forEach((dataset) => {
    if (dataset !== undefined) {
      result += createDatasetTooltip(
        dataset,
        currentPoint.dataIndex,
        formatUserCurrency,
      );
    }
  });

  return result;
};
